import { useEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { movies } from "@/mocks/movies";
import { articles, latestArticles } from "@/mocks/articles";

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  const movie = movies.find((m) => m.id === id);
  const article = articles.find((a) => a.id === id);
  const relatedArticles = latestArticles
    .filter((a) => a.id !== id)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <header>
        <Navbar />
      </header>

      <main className="pt-20">
        <div className="px-4 md:px-6 py-6">
          <div className="max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              {movie && (
                <>
                  <Link to={`/category/${movie.categorySlug}`} className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                    {movie.category}片
                  </Link>
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </>
              )}
              <span className="text-foreground-700 font-medium line-clamp-1">
                {movie ? movie.title : article ? article.title : "详情"}
              </span>
            </nav>

            {movie && (
              <article>
                <div className="flex flex-col md:flex-row gap-8 mb-10">
                  <div className="flex-shrink-0 w-full md:w-72">
                    <div className="aspect-[2/3] rounded-xl overflow-hidden">
                      <img
                        src={movie.image}
                        alt={movie.title}
                        title={`${movie.title} - 555电影网`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                      {movie.title}
                    </h1>
                    <p className="text-sm text-foreground-500 mb-4">
                      {movie.englishTitle}
                    </p>
                    <div className="flex items-center gap-3 mb-6">
                      <span className="text-sm font-bold text-accent-500 bg-accent-100 px-3 py-1 rounded-full whitespace-nowrap">
                        {movie.rating} 分
                      </span>
                      <span className="text-sm text-foreground-400">{movie.year}</span>
                      <span className="text-sm text-foreground-400">{movie.duration}</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                      <div>
                        <span className="text-xs text-foreground-400">导演</span>
                        <p className="text-sm text-foreground-800 font-medium">{movie.director}</p>
                      </div>
                      <div>
                        <span className="text-xs text-foreground-400">类型</span>
                        <p className="text-sm text-foreground-800 font-medium">{movie.category}</p>
                      </div>
                      <div className="sm:col-span-2">
                        <span className="text-xs text-foreground-400">主演</span>
                        <p className="text-sm text-foreground-800 font-medium">
                          {movie.actors.join(" · ")}
                        </p>
                      </div>
                    </div>

                    <div className="prose prose-sm max-w-none">
                      <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-2">
                        剧情简介
                      </h2>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {movie.description}
                      </p>
                    </div>
                  </div>
                </div>
              </article>
            )}

            {article && !movie && (
              <article>
                <div className="mb-6">
                  <span className="text-xs bg-secondary-100 text-secondary-900 px-3 py-1 rounded-full whitespace-nowrap">
                    {article.category}
                  </span>
                </div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-400 mb-8">
                  <span>{article.date}</span>
                  <span>{article.author}</span>
                </div>

                <div className="aspect-[16/9] rounded-xl overflow-hidden mb-8">
                  <img
                    src={article.image}
                    alt={article.title}
                    title={`${article.title} - 555电影网`}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div
                  className="prose prose-sm max-w-none text-foreground-700 leading-relaxed space-y-4"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                <div className="flex items-center gap-2 flex-wrap mt-8 pt-6 border-t border-background-200/70">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full whitespace-nowrap"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </article>
            )}

            {!movie && !article && (
              <div className="text-center py-20">
                <p className="text-foreground-500 text-lg">内容未找到</p>
                <Link
                  to="/"
                  className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  返回首页
                </Link>
              </div>
            )}

            {relatedArticles.length > 0 && (
              <section className="mt-14 pt-10 border-t border-background-200/70">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group cursor-pointer rounded-xl overflow-hidden bg-background-100 hover:bg-background-200/70 transition-colors"
                    >
                      <div className="aspect-[16/9] overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          title={`${ra.title} - 555电影网`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
                          {ra.title}
                        </h3>
                        <p className="text-xs text-foreground-400 mt-1">{ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}